import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Brain,
  Home,
  Lightbulb,
  FileText,
  Rocket,
  Target,
  Trophy,
  ArrowLeft,
  Calendar,
  TrendingUp,
  Sparkles,
  Filter,
  SortAsc,
  SortDesc,
  Loader2,
  ChevronRight,
  Star,
  Clock,
  BarChart3,
  Users,
  Download,
  ClipboardList,
  RefreshCw,
  CheckCircle2,
  Circle,
  X,
  Play,
  Pause
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkRefresh } from '../contexts/WorkRefreshContext';
import { SkeletonList } from '../components/ui/skeleton';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Filter/View types
const VIEWS = {
  ALL: 'all',
  SESSIONS: 'sessions',
  SESSIONS_COMPLETED: 'sessions_completed',
  SESSIONS_ONGOING: 'sessions_ongoing',
  EVALUATIONS: 'evaluations',
  SUBMISSIONS: 'submissions',
  IMPACT_LABS: 'impact_labs',
  IMPACT_LABS_ACTIVE: 'impact_labs_active',
  IMPACT_LABS_COMPLETED: 'impact_labs_completed'
};

const MyWorkPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { refreshTrigger } = useWorkRefresh();
  
  // Get view from URL or default to 'all' - use a function to get initial state
  const [activeView, setActiveView] = useState(() => {
    // Read from URL on initial render
    const params = new URLSearchParams(window.location.search);
    return params.get('view') || VIEWS.ALL;
  });
  const [loading, setLoading] = useState(true);
  
  // Sync activeView with URL params when they change (for navigation)
  useEffect(() => {
    const viewFromUrl = searchParams.get('view');
    // Only update if URL has a view param, otherwise keep current view
    if (viewFromUrl && viewFromUrl !== activeView) {
      setActiveView(viewFromUrl);
    }
  }, [searchParams, activeView]);
  const [workData, setWorkData] = useState({
    sessions: [],
    evaluations: [],
    submissions: [],
    impactLabs: []
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Update URL when view changes
  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === VIEWS.ALL) {
      searchParams.delete('view');
    } else {
      searchParams.set('view', view);
    }
    setSearchParams(searchParams);
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch all user work
  useEffect(() => {
    const fetchAllWork = async () => {
      if (!isAuthenticated) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/my-work`, {
          withCredentials: true
        });
        setWorkData(response.data);
      } catch (error) {
        console.error('Error fetching work data:', error);
        toast({
          title: "Error",
          description: "Failed to load your work. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchAllWork();
    }
  }, [isAuthenticated, refreshTrigger]);

  // Stats calculations
  const stats = useMemo(() => ({
    totalSessions: workData.sessions?.length || 0,
    completedSessions: workData.sessions?.filter(s => s.completed).length || 0,
    ongoingSessions: workData.sessions?.filter(s => !s.completed).length || 0,
    totalEvaluations: workData.evaluations?.length || 0,
    totalSubmissions: workData.submissions?.length || 0,
    totalImpactLabs: workData.impactLabs?.length || 0,
    activeImpactLabs: workData.impactLabs?.filter(l => l.status === 'active').length || 0,
    completedImpactLabs: workData.impactLabs?.filter(l => l.status === 'completed').length || 0,
    avgOI: (() => {
      const scores = workData.sessions
        ?.filter(s => s.oi_scores?.total_oi)
        .map(s => s.oi_scores.total_oi) || [];
      return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
    })()
  }), [workData]);

  // Get filtered items based on active view
  const filteredItems = useMemo(() => {
    let items = [];
    let itemType = 'mixed';

    switch (activeView) {
      case VIEWS.SESSIONS:
        items = workData.sessions || [];
        itemType = 'session';
        break;
      case VIEWS.SESSIONS_COMPLETED:
        items = (workData.sessions || []).filter(s => s.completed);
        itemType = 'session';
        break;
      case VIEWS.SESSIONS_ONGOING:
        items = (workData.sessions || []).filter(s => !s.completed);
        itemType = 'session';
        break;
      case VIEWS.EVALUATIONS:
        items = workData.evaluations || [];
        itemType = 'evaluation';
        break;
      case VIEWS.SUBMISSIONS:
        items = workData.submissions || [];
        itemType = 'submission';
        break;
      case VIEWS.IMPACT_LABS:
        items = workData.impactLabs || [];
        itemType = 'impactLab';
        break;
      case VIEWS.IMPACT_LABS_ACTIVE:
        items = (workData.impactLabs || []).filter(l => l.status === 'active');
        itemType = 'impactLab';
        break;
      case VIEWS.IMPACT_LABS_COMPLETED:
        items = (workData.impactLabs || []).filter(l => l.status === 'completed');
        itemType = 'impactLab';
        break;
      case VIEWS.ALL:
      default:
        // Combine all with type markers
        items = [
          ...(workData.sessions || []).map(s => ({ ...s, _type: 'session' })),
          ...(workData.evaluations || []).map(e => ({ ...e, _type: 'evaluation' })),
          ...(workData.submissions || []).map(s => ({ ...s, _type: 'submission' })),
          ...(workData.impactLabs || []).map(l => ({ ...l, _type: 'impactLab' }))
        ];
        itemType = 'mixed';
    }

    // Sort items
    return [...items].sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') {
        comparison = new Date(b.created_at || 0) - new Date(a.created_at || 0);
      } else if (sortBy === 'score') {
        const scoreA = a.oi_scores?.total_oi || a.oi_total || a.oi_score || 0;
        const scoreB = b.oi_scores?.total_oi || b.oi_total || b.oi_score || 0;
        comparison = scoreB - scoreA;
      } else if (sortBy === 'title') {
        const titleA = a.title || a.paper_title || '';
        const titleB = b.title || b.paper_title || '';
        comparison = titleA.localeCompare(titleB);
      }
      return sortOrder === 'desc' ? comparison : -comparison;
    });
  }, [activeView, workData, sortBy, sortOrder]);

  // View title mapping
  const getViewTitle = () => {
    const titles = {
      [VIEWS.ALL]: 'All Work',
      [VIEWS.SESSIONS]: 'All Think Lab Sessions',
      [VIEWS.SESSIONS_COMPLETED]: 'Completed Sessions',
      [VIEWS.SESSIONS_ONGOING]: 'Ongoing Sessions',
      [VIEWS.EVALUATIONS]: 'Paper Evaluations',
      [VIEWS.SUBMISSIONS]: 'Challenge Submissions',
      [VIEWS.IMPACT_LABS]: 'All Impact Labs',
      [VIEWS.IMPACT_LABS_ACTIVE]: 'Active Impact Labs',
      [VIEWS.IMPACT_LABS_COMPLETED]: 'Completed Impact Labs'
    };
    return titles[activeView] || 'My Work';
  };

  const getScoreColor = (score) => {
    if (score >= 9) return 'text-emerald-600';
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-blue-600';
    return 'text-slate-600';
  };

  const getScoreBadgeVariant = (score) => {
    if (score >= 9) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (score >= 8) return 'bg-green-100 text-green-700 border-green-300';
    if (score >= 6) return 'bg-blue-100 text-blue-700 border-blue-300';
    return 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Stat Card Component - Clickable
  const StatCard = ({ icon: Icon, value, label, color, view, isActive, subStats }) => (
    <Card 
      className={`border-2 transition-all cursor-pointer hover:shadow-lg ${
        isActive 
          ? `border-${color}-500 ring-2 ring-${color}-200 shadow-md` 
          : `border-${color}-200 hover:border-${color}-400`
      } bg-gradient-to-br from-${color}-50 to-white`}
      onClick={() => handleViewChange(view)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <Icon className={`w-6 h-6 text-${color}-600`} />
          {isActive && (
            <Badge className={`bg-${color}-600 text-white text-xs`}>Active</Badge>
          )}
        </div>
        <p className={`text-2xl font-bold text-${color}-700 mt-2`}>{value}</p>
        <p className="text-xs text-slate-600">{label}</p>
        
        {/* Sub-stats for clickable drill-down */}
        {subStats && (
          <div className="flex gap-2 mt-2 pt-2 border-t border-slate-200">
            {subStats.map((sub, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewChange(sub.view);
                }}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all ${
                  activeView === sub.view
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {sub.icon && <sub.icon className="w-3 h-3" />}
                <span>{sub.value}</span>
                <span className="text-slate-400">{sub.label}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render item card based on type
  const renderItemCard = (item) => {
    const type = item._type || 'session';
    
    switch (type) {
      case 'session':
        return renderSessionCard(item);
      case 'evaluation':
        return renderEvaluationCard(item);
      case 'submission':
        return renderSubmissionCard(item);
      case 'impactLab':
        return renderImpactLabCard(item);
      default:
        return null;
    }
  };

  // Render session card
  const renderSessionCard = (session) => (
    <Card 
      key={session.id}
      className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-purple-300 group"
      onClick={() => navigate(`/session/${session.id}`)}
    >
      <div className="h-1.5 bg-gradient-to-r from-purple-500 to-pink-500"></div>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <Badge className={session.completed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
            {session.completed ? (
              <><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</>
            ) : (
              <><Play className="w-3 h-3 mr-1" /> In Progress</>
            )}
          </Badge>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(session.created_at)}
          </span>
        </div>
        <CardTitle className="text-base font-semibold line-clamp-2 group-hover:text-purple-700 transition-colors">
          {session.title || 'Untitled Session'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600 line-clamp-2">
          {session.initial_thought || session.thinking_content?.substring(0, 150) || 'No content yet'}
        </p>
        <div className="flex items-center justify-between">
          {session.oi_scores?.total_oi ? (
            <Badge className={getScoreBadgeVariant(session.oi_scores.total_oi)}>
              OI: {session.oi_scores.total_oi.toFixed(1)}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">Not Evaluated</Badge>
          )}
          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );

  // Render evaluation card
  const renderEvaluationCard = (evaluation) => (
    <Card 
      key={evaluation.evaluation_id || evaluation.id}
      className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-blue-300 group"
      onClick={() => navigate(`/evaluation/${evaluation.evaluation_id || evaluation.id}`)}
    >
      <div className="h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <Badge className={getScoreBadgeVariant(evaluation.oi_total || evaluation.oi_score || 0)}>
            OI: {(evaluation.oi_total || evaluation.oi_score || 0).toFixed(1)}
          </Badge>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(evaluation.created_at)}
          </span>
        </div>
        <CardTitle className="text-base font-semibold line-clamp-2 group-hover:text-blue-700 transition-colors">
          {evaluation.paper_title || 'Untitled Evaluation'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600 line-clamp-2">
          {evaluation.paper_abstract || 'No abstract available'}
        </p>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs">
            <FileText className="w-3 h-3 mr-1" />
            {evaluation.paper_type || 'Research'}
          </Badge>
          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );

  // Render submission card
  const renderSubmissionCard = (submission) => (
    <Card 
      key={submission.id}
      className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-teal-300 group"
      onClick={() => navigate(`/challenges/${submission.challenge_id}`)}
    >
      <div className="h-1.5 bg-gradient-to-r from-teal-500 to-green-500"></div>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <Badge className={getScoreBadgeVariant(submission.oi_score || 0)}>
            OI: {(submission.oi_score || 0).toFixed(1)}
          </Badge>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(submission.created_at)}
          </span>
        </div>
        <CardTitle className="text-base font-semibold line-clamp-2 group-hover:text-teal-700 transition-colors">
          {submission.title || 'Untitled Submission'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600 line-clamp-2">
          {submission.summary || 'No summary available'}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Star className="w-3 h-3" /> {submission.likes || 0}
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );

  // Render impact lab card
  const renderImpactLabCard = (lab) => (
    <Card 
      key={lab.id}
      className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-orange-300 group"
      onClick={() => navigate(`/impact-lab/${lab.id}`)}
    >
      <div className="h-1.5 bg-gradient-to-r from-orange-500 to-red-500"></div>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <Badge 
            className={`text-xs ${
              lab.status === 'completed' ? 'bg-green-100 text-green-700' :
              lab.status === 'active' ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700'
            }`}
          >
            {lab.status === 'forming' ? 'Forming' : 
             lab.status === 'active' ? `Day ${lab.current_day || 1}/7` : 
             'Completed'}
          </Badge>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(lab.created_at)}
          </span>
        </div>
        <CardTitle className="text-base font-semibold line-clamp-2 group-hover:text-orange-700 transition-colors">
          {lab.challenge_title || lab.title || 'Untitled Lab'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <span className="text-xs text-slate-600">
            {lab.member_count || 1} member{(lab.member_count || 1) !== 1 ? 's' : ''}
          </span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform ml-auto" />
      </CardContent>
    </Card>
  );

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="h-8 w-48 bg-slate-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-slate-200 rounded animate-pulse"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="h-6 w-6 bg-slate-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-8 w-16 bg-slate-200 rounded mb-1 animate-pulse"></div>
                  <div className="h-3 w-20 bg-slate-200 rounded animate-pulse"></div>
                </CardContent>
              </Card>
            ))}
          </div>
          <SkeletonList count={4} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
            My Work
          </h1>
          <p className="text-slate-600 mt-1">
            All your creative work in one place • Click any stat to filter
          </p>
        </div>

        {/* Clickable Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {/* Total Sessions with sub-filters */}
          <StatCard 
            icon={Lightbulb}
            value={stats.totalSessions}
            label="Think Lab Sessions"
            color="purple"
            view={VIEWS.SESSIONS}
            isActive={activeView === VIEWS.SESSIONS}
            subStats={[
              { value: stats.completedSessions, label: 'done', icon: CheckCircle2, view: VIEWS.SESSIONS_COMPLETED },
              { value: stats.ongoingSessions, label: 'ongoing', icon: Play, view: VIEWS.SESSIONS_ONGOING }
            ]}
          />
          
          {/* Completed Sessions */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              activeView === VIEWS.SESSIONS_COMPLETED 
                ? 'border-green-500 ring-2 ring-green-200 shadow-md' 
                : 'border-green-200 hover:border-green-400'
            } bg-gradient-to-br from-green-50 to-white`}
            onClick={() => handleViewChange(VIEWS.SESSIONS_COMPLETED)}
          >
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-700">{stats.completedSessions}</p>
              <p className="text-xs text-slate-600">Completed</p>
              {activeView === VIEWS.SESSIONS_COMPLETED && (
                <Badge className="mt-2 bg-green-600 text-white text-xs">Active Filter</Badge>
              )}
            </CardContent>
          </Card>
          
          {/* Evaluations */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              activeView === VIEWS.EVALUATIONS 
                ? 'border-blue-500 ring-2 ring-blue-200 shadow-md' 
                : 'border-blue-200 hover:border-blue-400'
            } bg-gradient-to-br from-blue-50 to-white`}
            onClick={() => handleViewChange(VIEWS.EVALUATIONS)}
          >
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-700">{stats.totalEvaluations}</p>
              <p className="text-xs text-slate-600">Evaluations</p>
              {activeView === VIEWS.EVALUATIONS && (
                <Badge className="mt-2 bg-blue-600 text-white text-xs">Active Filter</Badge>
              )}
            </CardContent>
          </Card>
          
          {/* Submissions */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              activeView === VIEWS.SUBMISSIONS 
                ? 'border-teal-500 ring-2 ring-teal-200 shadow-md' 
                : 'border-teal-200 hover:border-teal-400'
            } bg-gradient-to-br from-teal-50 to-white`}
            onClick={() => handleViewChange(VIEWS.SUBMISSIONS)}
          >
            <CardContent className="p-4 text-center">
              <Rocket className="w-6 h-6 mx-auto text-teal-600 mb-2" />
              <p className="text-2xl font-bold text-teal-700">{stats.totalSubmissions}</p>
              <p className="text-xs text-slate-600">Submissions</p>
              {activeView === VIEWS.SUBMISSIONS && (
                <Badge className="mt-2 bg-teal-600 text-white text-xs">Active Filter</Badge>
              )}
            </CardContent>
          </Card>
          
          {/* Impact Labs */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              activeView.startsWith('impact_labs') 
                ? 'border-orange-500 ring-2 ring-orange-200 shadow-md' 
                : 'border-orange-200 hover:border-orange-400'
            } bg-gradient-to-br from-orange-50 to-white`}
            onClick={() => handleViewChange(VIEWS.IMPACT_LABS)}
          >
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto text-orange-600 mb-2" />
              <p className="text-2xl font-bold text-orange-700">{stats.totalImpactLabs}</p>
              <p className="text-xs text-slate-600">Impact Labs</p>
              {activeView.startsWith('impact_labs') && (
                <Badge className="mt-2 bg-orange-600 text-white text-xs">Active Filter</Badge>
              )}
            </CardContent>
          </Card>
          
          {/* Avg OI Score */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              activeView === VIEWS.ALL 
                ? 'border-emerald-500 ring-2 ring-emerald-200 shadow-md' 
                : 'border-emerald-200 hover:border-emerald-400'
            } bg-gradient-to-br from-emerald-50 to-white`}
            onClick={() => handleViewChange(VIEWS.ALL)}
          >
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-emerald-700">{stats.avgOI}</p>
              <p className="text-xs text-slate-600 truncate">Avg OI Score</p>
              {activeView === VIEWS.ALL && (
                <Badge className="mt-2 bg-emerald-600 text-white text-xs">All</Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Active Filter & Sort Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-slate-800">{getViewTitle()}</h2>
            <Badge variant="outline" className="text-sm">
              {filteredItems.length} items
            </Badge>
            {activeView !== VIEWS.ALL && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewChange(VIEWS.ALL)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-4 h-4 mr-1" />
                Clear filter
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border rounded-lg px-3 py-1.5 bg-white"
            >
              <option value="date">Date</option>
              <option value="score">OI Score</option>
              <option value="title">Title</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            >
              {sortOrder === 'desc' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Items Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredItems.map(renderItemCard)}
          </div>
        ) : (
          <Card className="border-2 border-dashed border-slate-300">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                {activeView.includes('session') ? <Lightbulb className="w-8 h-8 text-slate-400" /> :
                 activeView.includes('evaluation') ? <FileText className="w-8 h-8 text-slate-400" /> :
                 activeView.includes('submission') ? <Rocket className="w-8 h-8 text-slate-400" /> :
                 activeView.includes('impact') ? <Target className="w-8 h-8 text-slate-400" /> :
                 <Brain className="w-8 h-8 text-slate-400" />}
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No items found</h3>
              <p className="text-slate-500 mb-4">
                {activeView === VIEWS.ALL 
                  ? "Start your innovation journey by creating your first session!"
                  : `You don't have any ${getViewTitle().toLowerCase()} yet.`}
              </p>
              <Button
                onClick={() => navigate('/session/new')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Start New Session
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyWorkPage;
